import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, GraduationCap, Lightbulb, RefreshCw } from "lucide-react";
import PublicNav from "../components/PublicNav";
import Footer from "../components/Footer";
import { useCms } from "../context/CmsContext";

export default function Accueil() {
    const cms = useCms();
    const t = (k, fb) => cms.get(k, fb);

    const solutions = [
        { icon: Compass, title: t("accueil.sol1_title", "Parcoursup"), desc: t("accueil.sol1_desc", "Choix des vœux, lettres de motivation, stratégies de candidature.") },
        { icon: GraduationCap, title: t("accueil.sol2_title", "Choix de spécialités"), desc: t("accueil.sol2_desc", "Collège, première, terminale, post-bac.") },
        { icon: Lightbulb, title: t("accueil.sol3_title", "Bilan d'orientation"), desc: t("accueil.sol3_desc", "Identification de vos forces, de vos valeurs, et des pistes d'avenir.") },
        { icon: RefreshCw, title: t("accueil.sol4_title", "Réorientation"), desc: t("accueil.sol4_desc", "Changement de voie.") },
    ];

    const steps = [
        { n: "01", text: t("accueil.step1", "Pour comprendre votre parcours et identifier vos besoins, nous vous proposons un premier entretien gratuit.") },
        { n: "02", text: t("accueil.step2", "Vous recevez notre proposition sur mesure, entièrement transparente : votre accompagnement, le contenu de chaque séance et le tarif adapté à votre situation, pour avancer en toute confiance.") },
        { n: "03", text: t("accueil.step3", "Des temps de travail dédiés à vos choix d'orientation, pour définir vos objectifs, construire votre projet et trouver la motivation pour les atteindre.") },
        { n: "04", text: t("accueil.step4", "Un bilan complet remis à l'issue de votre accompagnement : une orientation claire et les clés pour construire votre avenir avec sérénité.") },
    ];

    return (
        <div data-testid="page-accueil" className="bg-ink-800 min-h-screen text-white">
            <PublicNav />

            <section className="relative">
                <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-[42px] lg:pt-32 lg:pb-[42px]">
                    <div className="max-w-3xl animate-fade-up">
                        <span className="inline-block text-sm tracking-wide text-ice mb-6">
                            {t("accueil.hero_eyebrow", "Conseil en orientation · Paris · Bordeaux")}
                        </span>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-white">
                            Trouvez <span className="text-ice">votre voie</span><span className="text-ice">,</span><br />
                            en confiance.
                        </h1>
                        <p className="mt-8 text-lg text-white/80 leading-relaxed max-w-2xl whitespace-pre-line">
                            {t("accueil.hero_description", "Votre parcours scolaire demande des choix d'orientation réguliers. Nous vous proposons un accompagnement humain et personnalisé. Écouter, accompagner, guider et motiver sont les valeurs qui fondent notre engagement auprès de chacun.")}
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to="/contact"
                                data-testid="hero-cta-rdv"
                                className="group inline-flex items-center gap-2 bg-ice text-white hover:bg-ice-soft hover:text-ink-900 transition-colors px-7 py-4 rounded-md font-medium tracking-wide"
                            >
                                {t("accueil.cta_primary", "Prendre rendez-vous")}
                                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </Link>
                            <Link
                                to="/accompagnements"
                                data-testid="hero-cta-services"
                                className="inline-flex items-center gap-2 border border-ice/50 text-white hover:border-ice hover:bg-ice/10 transition-colors px-7 py-4 rounded-md tracking-wide"
                            >
                                {t("accueil.cta_secondary", "Découvrir nos accompagnements")}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-[42px] px-6 md:px-12 border-t border-white/5">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <span className="font-serif text-2xl sm:text-3xl text-ice block mb-3">{t("accueil.solutions_label", "Nos Solutions")}</span>
                            <h2 className="font-serif text-4xl sm:text-5xl">
                                À chaque étape, un <span className="text-ice">accompagnement</span> dédié.
                            </h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {solutions.map((s, i) => (
                            <div
                                key={i}
                                data-testid={`solution-card-${i}`}
                                className="group p-8 rounded-xl bg-ink-700 border border-ice/15 hover:border-ice hover:bg-ink-600 transition-all duration-300 cursor-default"
                            >
                                <s.icon size={28} className="text-ice group-hover:text-ice-soft transition-colors" />
                                <h3 className="font-serif text-2xl mt-6 text-white">{s.title}</h3>
                                <p className="mt-3 text-base text-white/80 leading-relaxed whitespace-pre-line">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-[42px] px-6 md:px-12 bg-ink-700/30 border-t border-white/5">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-12">
                        <span className="font-serif text-2xl sm:text-3xl text-ice block mb-3">{t("accueil.steps_label", "Les Étapes")}</span>
                        <h2 className="font-serif text-4xl sm:text-5xl">
                            Un parcours <span className="text-ice">clair</span><span className="text-ice">,</span> du premier contact au bilan.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16">
                        {steps.map((s) => (
                            <div key={s.n} data-testid={`step-${s.n}`} className="flex gap-6 items-start">
                                <div className="font-serif text-6xl text-ice/70 leading-none shrink-0 w-20">{s.n}</div>
                                <p className="text-base text-white/85 leading-relaxed pt-3 border-t border-ice/30 whitespace-pre-line">
                                    {s.text}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
